import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateAccessSelector } from '../../redux/common/roleAccess/selectors';
import { updateFormStatusSelector } from '../../redux/common/formValueChange/selectors';
import BrandingColors from '../../styles/color';
import MODULE_LINKS, { MODULE_ORDER, MODULE_DISPLAY_NAMES } from '../../config/globalSearchModuleLinks';
import { MODULE_TO_ORDER, buildDeepLinkGateUrl } from '../../helper/constants';
import {
    NAVIGATION_ENTITY_TYPE,
    NAVIGATION_KEY_SEPARATOR,
    findNavigationLocation,
    openNavigationLocation
} from './navigationLocations';

const useStyles = makeStyles(() => ({
    root: {
        padding: '8px 12px',
        borderBottom: `1px solid ${BrandingColors.customGrey}`
    },
    rootPage: {
        padding: '12px 0',
        borderBottom: `1px solid ${BrandingColors.customGrey}`
    },
    header: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '13px',
        fontWeight: 600,
        color: BrandingColors.darkGreyFont,
        marginBottom: '4px'
    },
    headerPage: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '15px',
        fontWeight: 600,
        color: BrandingColors.darkGreyFont,
        marginBottom: '2px'
    },
    entityType: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '12px',
        color: BrandingColors.lightGreyFont,
        marginBottom: '4px'
    },
    subtitle: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '11px',
        color: BrandingColors.lightGreyFont,
        marginBottom: '6px'
    },
    moduleSection: {
        marginTop: '6px'
    },
    moduleLabel: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '11px',
        fontWeight: 600,
        color: BrandingColors.lightGreyFont,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        marginBottom: '2px'
    },
    moduleLabelPage: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '13px',
        fontWeight: 600,
        color: BrandingColors.darkGreyFont,
        marginBottom: '2px',
        marginLeft: '8px'
    },
    subGroupLabel: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '11px',
        color: BrandingColors.blkSideBarFont,
        marginLeft: '8px',
        marginTop: '2px'
    },
    subGroupLabelPage: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '12px',
        color: BrandingColors.blkSideBarFont,
        marginLeft: '16px',
        marginTop: '2px'
    },
    linksRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginLeft: '8px'
    },
    linksRowPage: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginLeft: '16px'
    },
    link: {
        fontFamily: 'FiraSans-Regular',
        fontSize: '12px',
        color: BrandingColors.linkTextFont,
        textDecoration: 'underline',
        cursor: 'pointer',
        padding: '2px 4px',
        borderRadius: '2px',
        border: 'none',
        background: 'transparent',
        '&:hover': {
            backgroundColor: BrandingColors.hoverBlue
        },
        '&:focus-visible': {
            outline: `2px solid ${BrandingColors.textBord}`,
            outlineOffset: '1px'
        }
    },
    pipeSeparator: {
        color: BrandingColors.lightGreyFont,
        margin: '0 2px',
        fontSize: '12px',
        userSelect: 'none'
    }
}));

// Display-label overrides for entity types whose key is not a single word —
// the default capitalize(entityKey) would render "mealplan" as "Mealplan".
const ENTITY_LABELS = {
    mealplan: 'Meal Plan',
    housingperiod: 'Housing Period',
    // WI 305242 — the two halves of Table Maintenance stay separate entity types
    // (different tables, different tabs) but share one label, exactly as the
    // acceptance criteria word it: "Glossary/Reference Table".
    glossarytable: 'Glossary/Reference Table',
    referencetable: 'Glossary/Reference Table'
};

// Builds an allowed-item index per module by matching each nested item's
// moduleorder against the root module. A sub-link is considered visible only
// when its parent module appears with access in parentLevel AND the sub-link's
// own menuItemName appears with access somewhere in the user's tree under the
// same moduleorder. Flat matching by menuItemName only is insufficient because
// many menuItemName values (Maintenance, Document Tracking, Change Address,
// Student Notes, etc.) are reused across several modules.
function buildPermissionIndex(access) {
    const result = {};
    Object.keys(MODULE_TO_ORDER).forEach((name) => {
        result[name] = { moduleAllowed: false, items: new Set() };
    });

    (access?.parentLevel || []).forEach((item) => {
        const name = typeof item?.menuItemName === 'string' ? item.menuItemName.trim() : '';
        if (name && item.menuAccess !== 0 && result[name]) {
            result[name].moduleAllowed = true;
        }
    });

    const childLevels = [
        access?.nestedParentLevel,
        access?.subNestedParentLevel,
        access?.subDeepNestedParentLevel
    ];
    childLevels.forEach((level) => {
        (level || []).forEach((item) => {
            const name = typeof item?.menuItemName === 'string' ? item.menuItemName.trim() : '';
            if (!name || item.menuAccess === 0) {
                return;
            }
            const moduleName = Object.keys(MODULE_TO_ORDER).find(
                (key) => MODULE_TO_ORDER[key] === item.moduleorder
            );
            if (moduleName && result[moduleName]) {
                result[moduleName].items.add(name);
            }
        });
    });

    return result;
}

const trimmedMenuName = (item) => (typeof item?.menuItemName === 'string' ? item.menuItemName.trim() : '');

// A sidebar screen: its label, "<Module> Screen", then the screen's breadcrumb with a single
// "View" link that opens it exactly as the sidebar would. The API only lists screens the
// user's menu renders; the link is still dropped when the screen is not in this user's
// sidebar tree (or the module filter excludes it), mirroring how record links are gated.
function NavigationLocationResult({ student, moduleFilter, onNavigate, context }) {
    const classes = useStyles();
    const history = useHistory();
    const { t } = useTranslation();
    const { sideBarItems } = useSelector(updateFormStatusSelector) || {};

    const isListbox = context === 'listbox';
    const navigationKey = student.navigationKey;
    const navLocation = useMemo(
        () => findNavigationLocation(sideBarItems, navigationKey),
        [sideBarItems, navigationKey]
    );

    const labels = navLocation ? navLocation.path.map((item) => t(item.name || trimmedMenuName(item)).trim()) : [];
    const label = labels.length > 0 ? labels[labels.length - 1] : student.fullName;
    const moduleLabel = labels.length > 0 ? labels[0] : '';
    const screenText = moduleLabel
        ? t('sidebar.search.navigationScreen', { module: moduleLabel })
        : t('sidebar.search.navigationScreenFallback');
    const breadcrumb = labels.join(NAVIGATION_KEY_SEPARATOR);

    const moduleName = navLocation ? navLocation.path[0].menuItemName?.trim() : '';
    const filteredOut = moduleFilter && moduleFilter !== 'All' && moduleName !== moduleFilter;
    const leaf = navLocation ? navLocation.path[navLocation.path.length - 1] : null;
    const canOpen = Boolean(leaf && !filteredOut && typeof leaf.url === 'string' && leaf.url.startsWith('/'));

    const rootClass = isListbox ? classes.root : classes.rootPage;
    const headerClass = isListbox ? classes.header : classes.headerPage;
    const moduleLabelClass = isListbox ? classes.moduleLabel : classes.moduleLabelPage;
    const linksRowClass = isListbox ? classes.linksRow : classes.linksRowPage;

    if (!canOpen) {
        return (
            <li role={isListbox ? "option" : undefined} aria-selected={isListbox ? "false" : undefined} className={rootClass}>
                <div className={headerClass}>{label}</div>
                <div className={classes.entityType}>{screenText}</div>
            </li>
        );
    }

    const handleView = () => {
        if (onNavigate) {
            onNavigate();
        }
        openNavigationLocation(history, leaf, navigationKey);
    };

    const viewLabel = t('sidebar.search.view');
    return (
        <li role={isListbox ? "none" : undefined} className={rootClass}>
            <div className={headerClass}>{label}</div>
            <div className={classes.entityType}>{screenText}</div>
            <div className={classes.moduleSection}>
                <div className={moduleLabelClass}>{breadcrumb}</div>
                <div className={linksRowClass}>
                    <button
                        id={`gsearch-opt-${NAVIGATION_ENTITY_TYPE}-${student.entityId}-view`}
                        type="button"
                        role={isListbox ? "option" : undefined}
                        aria-selected={isListbox ? "false" : undefined}
                        aria-label={`${label}, ${screenText}, ${breadcrumb}, ${viewLabel}`}
                        className={classes.link}
                        onClick={handleView}
                    >
                        {viewLabel}
                    </button>
                </div>
            </div>
        </li>
    );
}

NavigationLocationResult.propTypes = {
    student: PropTypes.shape({
        entityId: PropTypes.number,
        fullName: PropTypes.string,
        navigationKey: PropTypes.string
    }).isRequired,
    moduleFilter: PropTypes.string,
    onNavigate: PropTypes.func,
    context: PropTypes.oneOf(['listbox', 'page'])
};

NavigationLocationResult.defaultProps = {
    moduleFilter: 'All',
    onNavigate: undefined,
    context: 'listbox'
};

function EntitySearchResult({ student, moduleFilter, onNavigate, entityType, context }) {
    const classes = useStyles();
    const history = useHistory();
    const { access } = useSelector(updateAccessSelector) || { access: {} };
    const allowed = useMemo(() => buildPermissionIndex(access), [access]);

    const isListbox = context === 'listbox';

    const entityKey = (entityType || student.entityType || 'student').toLowerCase();
    const orderedModules = MODULE_ORDER[entityKey] || [];
    const linksByModule = MODULE_LINKS[entityKey] || {};

    // Entity-agnostic identity fields. New results carry entityId/idDisplay; older
    // (student-only) payloads still expose studentUID/studentIdDisplay. As a last
    // resort fall back to the numeric idDisplay so a partially-populated row never
    // produces a null id in the deep-link route.
    const idDisplay = student.idDisplay ?? student.studentIdDisplay;
    const idFromDisplay = idDisplay != null && /^\d+$/.test(String(idDisplay)) ? Number(idDisplay) : null;
    const entityId = student.entityId ?? student.studentUID ?? idFromDisplay;
    const entityLabel = ENTITY_LABELS[entityKey] || (entityKey.charAt(0).toUpperCase() + entityKey.slice(1));
    const headerText = idDisplay ? `${student.fullName} (ID: ${idDisplay})` : student.fullName;

    const visibleModules = useMemo(() => {
        const filter = moduleFilter === 'All' ? null : moduleFilter;
        return orderedModules.filter((moduleName) => {
            if (filter && moduleName !== filter) return false;
            const perm = allowed[moduleName];
            if (!perm || !perm.moduleAllowed) return false;
            const links = linksByModule[moduleName] || [];
            return links.some((link) => perm.items.has(link.menuItemName));
        });
    }, [moduleFilter, allowed, orderedModules, linksByModule]);

    const rootClass = isListbox ? classes.root : classes.rootPage;
    const headerClass = isListbox ? classes.header : classes.headerPage;
    const moduleLabelClass = isListbox ? classes.moduleLabel : classes.moduleLabelPage;
    const subGroupClass = isListbox ? classes.subGroupLabel : classes.subGroupLabelPage;
    const linksRowClass = isListbox ? classes.linksRow : classes.linksRowPage;

    if (visibleModules.length === 0) {
        return (
            <li role={isListbox ? "option" : undefined} aria-selected={isListbox ? "false" : undefined} className={rootClass}>
                <div className={headerClass}>{headerText}</div>
                <div className={classes.entityType}>{entityLabel}</div>
            </li>
        );
    }

    const handleClick = (path) => {
        // Guard: never navigate with a missing id — that builds a broken
        // /deeplink/<type>/null/go... route (and lands on /admissions/prospects/null).
        if (entityId === null || entityId === undefined || entityId === '') {
            return;
        }
        if (onNavigate) {
            onNavigate();
        }
        // Replace {studentUID} placeholder with actual value for pages that use URL params
        const resolvedPath = path.replace('{studentUID}', entityId);
        const targetPath = resolvedPath.startsWith('/') ? resolvedPath : `/${resolvedPath}`;
        history.push(buildDeepLinkGateUrl(entityKey, entityId, targetPath));
    };

    return (
        <li role={isListbox ? "none" : undefined} className={rootClass}>
            <div className={headerClass}>{headerText}</div>
            <div className={classes.entityType}>{entityLabel}</div>

            {visibleModules.map((moduleName) => {
                const perm = allowed[moduleName];
                const links = (linksByModule[moduleName] || []).filter((link) => perm && perm.items.has(link.menuItemName));
                if (links.length === 0) return null;
                const groups = new Map();
                links.forEach((link) => {
                    const groupName = link.subGroup || '';
                    if (!groups.has(groupName)) groups.set(groupName, []);
                    groups.get(groupName).push(link);
                });

                const moduleLabel = (MODULE_DISPLAY_NAMES[entityKey] && MODULE_DISPLAY_NAMES[entityKey][moduleName]) || moduleName;

                return (
                    <div key={moduleName} className={classes.moduleSection}>
                        <div className={moduleLabelClass}>{moduleLabel}</div>
                        {[...groups.entries()].map(([groupName, groupLinks]) => (
                            <div key={groupName || 'default'}>
                                {groupName && <div className={subGroupClass}>{groupName}</div>}
                                <div className={linksRowClass}>
                                    {groupLinks.map((link, linkIndex) => {
                                        const optionId = `gsearch-opt-${entityKey}-${entityId}-${moduleName.replace(/\s/g, '')}-${link.key}`;
                                        return (
                                            <React.Fragment key={link.key}>
                                                {!isListbox && linkIndex > 0 && (
                                                    <span className={classes.pipeSeparator} aria-hidden="true">|</span>
                                                )}
                                                <button
                                                    id={optionId}
                                                    type="button"
                                                    role={isListbox ? "option" : undefined}
                                                    aria-selected={isListbox ? "false" : undefined}
                                                    aria-label={`${student.fullName}, ${entityLabel}${idDisplay ? `, ID ${idDisplay}` : ''}, ${moduleLabel}, ${link.displayName || link.menuItemName}`}
                                                    className={classes.link}
                                                    onClick={() => handleClick(link.path)}
                                                >
                                                    {link.displayName || link.menuItemName}
                                                </button>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })}
        </li>
    );
}

EntitySearchResult.propTypes = {
    student: PropTypes.object.isRequired,
    moduleFilter: PropTypes.string,
    onNavigate: PropTypes.func,
    entityType: PropTypes.string,
    context: PropTypes.oneOf(['listbox', 'page'])
};

// One global-search result row. Sidebar screens have their own layout; every record type
// (student, prospect, meal plan, …) renders its module links.
function StudentSearchResult(props) {
    const { student, entityType } = props;
    const entityKey = (entityType || student.entityType || 'student').toLowerCase();
    if (entityKey === NAVIGATION_ENTITY_TYPE) {
        return <NavigationLocationResult {...props} />;
    }
    return <EntitySearchResult {...props} />;
}

StudentSearchResult.propTypes = {
    student: PropTypes.shape({
        entityType: PropTypes.string,
        entityId: PropTypes.number,
        idDisplay: PropTypes.string,
        studentUID: PropTypes.number,
        studentIdDisplay: PropTypes.string,
        fullName: PropTypes.string,
        matchedField: PropTypes.string,
        navigationKey: PropTypes.string
    }).isRequired,
    moduleFilter: PropTypes.string,
    onNavigate: PropTypes.func,
    entityType: PropTypes.string,
    context: PropTypes.oneOf(['listbox', 'page'])
};

StudentSearchResult.defaultProps = {
    moduleFilter: 'All',
    onNavigate: undefined,
    entityType: 'student',
    context: 'listbox'
};

export default StudentSearchResult;
